import React, { useState, useEffect, useCallback, useRef } from 'react';
import "./_searchBar.scss";

// Custom hook for debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function SearchBar({ 
  value, 
  onChange, 
  placeholder = 'Search...', 
  className = '',
  debounceDelay = 0, // 0 means no debounce
  onDebouncedChange = null // Optional callback for debounced changes
}) {
  const [localValue, setLocalValue] = useState(value || '');
  const debouncedValue = useDebounce(localValue, debounceDelay);
  const inputRef = useRef(null);

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  // Handle debounced changes
  useEffect(() => {
    if (debounceDelay > 0 && onDebouncedChange) {
      onDebouncedChange(debouncedValue);
    }
  }, [debouncedValue, debounceDelay, onDebouncedChange]);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // If no debounce, call onChange immediately
    if (debounceDelay === 0) {
      onChange(newValue);
    }
  }, [onChange, debounceDelay]);

  const handleClear = useCallback(() => {
    // Clear local state immediately
    setLocalValue('');
  }, [onChange, onDebouncedChange, debounceDelay]);

  return (
    <div className={`search-wrapper ${className}`}>
      <input
        ref={inputRef}
        className="search"
        type="text"
        placeholder={placeholder}
        value={localValue}
        onChange={handleInputChange}
      />
      {localValue && (
        <button className="clear-button" onClick={handleClear} type="button">
          ×
        </button>
      )}
    </div>
  );
}

export default SearchBar;
